/**
 * Parser de búsqueda estilo Gmail — versión cliente.
 * Espejo de `apps/backend/src/common/search-query.parser.ts` para que
 * el frontend pueda extraer operadores y mostrar chips + highlight.
 */

export type GmailSearchFilters = {
  from?: string
  to?: string
  subject?: string
  body?: string
  hasAttachment?: boolean
  hasAlert?: boolean
  hasCase?: boolean
  isUnread?: boolean
  isRead?: boolean
  isStarred?: boolean
  isUnstarred?: boolean
  status?: string
  label?: string
  afterDate?: string  // ISO date string
  beforeDate?: string
  freeText?: string
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/
const RELATIVE_DATE = /^(\d+)([dwm])$/i

export function parseGmailQuery(input: string): { filters: GmailSearchFilters; cleanQuery: string } {
  const filters: GmailSearchFilters = {}
  const remainingTokens: string[] = []
  const tokens = tokenize(input)

  for (const token of tokens) {
    const match = /^([a-z_]+):(.*)$/i.exec(token)
    if (!match) {
      remainingTokens.push(token)
      continue
    }
    const [, op, rawValue] = match
    const value = rawValue.replace(/^["']|["']$/g, '').trim()
    if (!value) continue

    switch (op.toLowerCase()) {
      case 'from': filters.from = value; break
      case 'to': filters.to = value; break
      case 'subject':
      case 'asunto': filters.subject = value; break
      case 'has':
        switch (value.toLowerCase()) {
          case 'attachment': case 'adjunto': case 'adjuntos':
            filters.hasAttachment = true; break
          case 'alert': case 'alerta': case 'alertas':
            filters.hasAlert = true; break
          case 'case': case 'caso': case 'casos':
            filters.hasCase = true; break
          case 'unread': filters.isUnread = true; break
          case 'star': case 'starred': case 'destacado':
            filters.isStarred = true; break
        }
        break
      case 'is':
        switch (value.toLowerCase()) {
          case 'unread': case 'no_leido': filters.isUnread = true; break
          case 'read': case 'leido': filters.isRead = true; break
          case 'starred': case 'destacado': filters.isStarred = true; break
          case 'unstarred': case 'no_destacado': filters.isUnstarred = true; break
        }
        break
      case 'status': case 'estado':
        filters.status = value.toLowerCase(); break
      case 'label': case 'etiqueta':
        filters.label = value; break
      case 'after': case 'desde': {
        const d = parseDate(value)
        if (d) filters.afterDate = d
        break
      }
      case 'before': case 'hasta': {
        const d = parseDate(value)
        if (d) filters.beforeDate = d
        break
      }
      case 'newer_than': case 'mas_nuevo_que': {
        const d = parseRelativeDate(value)
        if (d) filters.afterDate = d
        break
      }
      case 'older_than': case 'mas_viejo_que': {
        const d = parseRelativeDate(value)
        if (d) filters.beforeDate = d
        break
      }
      default:
        remainingTokens.push(token)
    }
  }

  const freeText = remainingTokens.join(' ').trim()
  if (freeText) {
    // IMPORTANTE: NO asignamos el freeText a `filters.subject` ni a `filters.body`.
    // El freeText queda solo en `freeText` y se usa como término de búsqueda general
    // (busca en subject + body + from + to en el backend).
    // Asignarlo a `subject` mostraba un chip "Asunto: 'X'" engañoso.
    filters.freeText = freeText
  }

  return { filters, cleanQuery: buildCleanQuery(filters) }
}

/**
 * Normaliza una query al formato canónico inglés.
 * - `asunto:X` → `subject:X`
 * - `etiqueta:X` → `label:X`
 * - `estado:X` → `status:X`
 * - `desde:X` → `after:X`
 * - `hasta:X` → `before:X`
 * - `mas_nuevo_que:X` → `newer_than:X`
 * - `mas_viejo_que:X` → `older_than:X`
 *
 * Si el usuario tipea un alias español, lo reescribimos al inglés canónico
 * para mantener consistencia visual en el input y en los chips.
 */
const OPERATOR_ALIASES: Record<string, string> = {
  asunto: 'subject',
  etiqueta: 'label',
  estado: 'status',
  desde: 'after',
  hasta: 'before',
  mas_nuevo_que: 'newer_than',
  mas_viejo_que: 'older_than',
}

export function normalizeQuery(input: string): string {
  if (!input) return ''
  // Reemplaza solo el operador (parte antes de ':'), preserva el value
  return input.replace(/\b([a-zA-Z_]+):/g, (match, op) => {
    const canonical = OPERATOR_ALIASES[op.toLowerCase()]
    return canonical ? `${canonical}:` : match
  })
}

function tokenize(input: string): string[] {
  const tokens: string[] = []
  let current = ''
  let inQuote: string | null = null
  for (let i = 0; i < input.length; i++) {
    const c = input[i]
    if (inQuote) {
      if (c === inQuote) inQuote = null
      else current += c
    } else if (c === '"' || c === "'") {
      inQuote = c
    } else if (c === ' ' || c === '\t') {
      if (current.trim()) {
        tokens.push(current)
        current = ''
      }
    } else {
      current += c
    }
  }
  if (current.trim()) tokens.push(current)
  return tokens
}

function parseDate(value: string): string | null {
  if (ISO_DATE.test(value)) return value
  const dmyMatch = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value)
  if (dmyMatch) {
    const [, d, m, y] = dmyMatch
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
  }
  return null
}

function parseRelativeDate(value: string): string | null {
  const m = RELATIVE_DATE.exec(value)
  if (!m) return null
  const n = parseInt(m[1], 10)
  const unit = m[2].toLowerCase()
  const days = unit === 'd' ? n : unit === 'w' ? n * 7 : n * 30
  const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
  return d.toISOString().slice(0, 10)
}

function buildCleanQuery(f: GmailSearchFilters): string {
  const parts: string[] = []
  if (f.from) parts.push(`from:${f.from}`)
  if (f.to) parts.push(`to:${f.to}`)
  if (f.subject) parts.push(`subject:${f.subject}`)
  if (f.status) parts.push(`status:${f.status}`)
  if (f.label) parts.push(`label:${f.label}`)
  if (f.isUnread) parts.push('is:unread')
  if (f.isRead) parts.push('is:read')
  if (f.isStarred) parts.push('is:starred')
  if (f.hasAttachment) parts.push('has:attachment')
  if (f.hasAlert) parts.push('has:alert')
  if (f.hasCase) parts.push('has:case')
  if (f.afterDate) parts.push(`after:${f.afterDate}`)
  if (f.beforeDate) parts.push(`before:${f.beforeDate}`)
  if (f.body) parts.push(f.body)
  return parts.join(' ')
}

export const SEARCH_OPERATORS = [
  { op: 'from:', desc: 'remitente', example: 'from:dian' },
  { op: 'to:', desc: 'destinatario', example: 'to:cliente' },
  { op: 'subject:', desc: 'texto en asunto', example: 'subject:tutela' },
  { op: 'has:attachment', desc: 'con adjuntos', example: 'has:attachment' },
  { op: 'has:alert', desc: 'con alertas', example: 'has:alert' },
  { op: 'has:case', desc: 'con caso', example: 'has:case' },
  { op: 'is:unread', desc: 'no leídos', example: 'is:unread' },
  { op: 'is:read', desc: 'leídos', example: 'is:read' },
  { op: 'is:starred', desc: 'destacados', example: 'is:starred' },
  { op: 'status:', desc: 'recibidos|procesados|en_caso', example: 'status:received' },
  { op: 'label:', desc: 'etiqueta MCI/AI/custom', example: 'label:AI/URGENTE' },
  { op: 'after:', desc: 'después de fecha (YYYY-MM-DD)', example: 'after:2026-01-01' },
  { op: 'before:', desc: 'antes de fecha', example: 'before:2026-12-31' },
  { op: 'newer_than:', desc: 'más reciente que (Nd|Nw|Nm)', example: 'newer_than:7d' },
  { op: 'older_than:', desc: 'más antiguo que', example: 'older_than:30d' },
] as const

const RECENT_SEARCHES_KEY = 'torre.recentSearches.v1'
const MAX_RECENT_SEARCHES = 8

export function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY)
    if (!raw) return []
    return JSON.parse(raw).filter((s: any) => typeof s === 'string').slice(0, MAX_RECENT_SEARCHES)
  } catch {
    return []
  }
}

export function addRecentSearch(query: string) {
  if (!query.trim()) return
  const recent = getRecentSearches().filter(s => s !== query)
  recent.unshift(query)
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT_SEARCHES)))
}

export function clearRecentSearches() {
  localStorage.removeItem(RECENT_SEARCHES_KEY)
}

/**
 * Resalta los términos de búsqueda en un texto.
 * Devuelve un array de strings/React nodes con <mark> en los matches.
 */
export function highlightMatches(text: string, query: string): Array<string | { match: string }> {
  if (!query || !text) return [text]
  const { filters } = parseGmailQuery(query)
  const terms: string[] = []
  if (filters.freeText) terms.push(...filters.freeText.split(/\s+/).filter(Boolean))
  if (filters.subject && filters.subject !== filters.freeText) terms.push(filters.subject)
  if (filters.from) terms.push(filters.from)
  if (filters.to) terms.push(filters.to)
  if (terms.length === 0) return [text]

  // Escapar regex
  const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  const re = new RegExp(`(${escaped.join('|')})`, 'gi')

  // Split conservando los matches
  const parts: Array<string | { match: string }> = []
  let lastIndex = 0
  const matches = text.matchAll(re)
  for (const m of matches) {
    if (m.index === undefined) continue
    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index))
    }
    parts.push({ match: m[0] })
    lastIndex = m.index + m[0].length
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }
  return parts
}
